import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Table, Space, Dropdown, notification, Input } from "antd";
import { DeleteOutlined, EditOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import DeptoForm from "../components/Forms/DeptoForm";
import axios from "axios";
import "moment-timezone";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import MessageDialog from "../components/MessageDialog";
import LoadingModal from "../utils/LoadingModal";
import formatTimestamp from "../utils/FormatTimestamp";

const PAGE_SIZE = 15;

const DepartmentTable = () => {
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeptoForm, setShowDeptoForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentDepto, setCurrentDepto] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });
  const [messageVisible, setMessageVisible] = useState(false);
  const [searchText, setSearchText] = useState("");

  const columns = [
    // {
    //   title: "ID",
    //   dataIndex: "id",
    //   key: "id",
    //   width: 80,
    // },
    {
      title: "Nombre del departamento",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Última actualización",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text) => formatTimestamp(text),
    },
    {
      title: "Opciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            trigger={["click"]}
            menu={{
              items:[
                {
                  key: '1',
                  label: 'Editar',
                  icon:<EditOutlined />,
                  onClick: () => handleActionClick("edit", record)
                },
                {
                  key: '2',
                  label: 'Eliminar',
                  icon:<DeleteOutlined />,
                  onClick: () => handleActionClick("delete", record)
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} shape="circle" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchData = useCallback(
    async (newPagination, search) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/consult-departments`,
          {
            headers: {
              "x-token": globals.token,
            },
            params: {
              page,
              limit: pageSize,
              search: search,
            },
          }
        );
        setData(response?.data?.data.rows);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data.count,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data.count,
          }
          );
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        notification.error({
          message: "Error obteniendo información!",
          placement: "bottomRight",
        });
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  useEffect(() => {
    fetchData(null);
  }, [fetchData]);

  const handleActionClick = async (key, record) => {
    if (key === "edit") {
      setIsEdit(true);
      setCurrentDepto(record);
      setShowDeptoForm(true);
    }
    else if (key === "delete") {
      setCurrentDepto(record);
      setMessageVisible(true);
    }
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination);
  }

  const saveUserForm = async ({status, newDepto}) => {
    setShowDeptoForm(false);
    if (isEdit) {
      setIsEdit(false);
      notification.success({
        message: "Depto. editado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }
    if(status === "created"){
      setData([newDepto, ...data]);
    }

    notification.success({
      message: "Depto. creado exitosamente!",
      placement: "bottomRight",
      duration: 2,
    });
  };

  const closeUserForm = () => {
    setShowDeptoForm(false);
    setIsEdit(false);
    setCurrentDepto(null);
  };

  const deleteUser = async () => {
    setMessageVisible(false);
    try{
      await axios.delete(`${globals.SERVICE_API_DB}/v1/delete-department/${currentDepto.id}`,
      {
        headers: {
          "x-token": globals.token,
        },
      });
      setData(data.filter((item) => item.id !== currentDepto.id));
      notification.success({
        message: "Depto. eliminado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
    }
    catch(err){
      if (err.response.data.status === -3) {
        notification.error({
          message: "El departamento que intenta borrar está asociado actualmente a un punto de plantilla o a una plantilla. ",
          placement: "bottomRight",
        });
      } else {
        notification.error({
          message: "Error eliminando departamento",
          placement: "bottomRight",
        });
      }
      return;
    }
  };

  const handleRefresh = () => {
    fetchData(null);
  };

  const handleSearch = async () => {
    fetchData(null, searchText);
  };

  const handleSearchEnterKey = async (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Departamentos</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={() => {
            setIsEdit(false);
            setShowDeptoForm(true);
          }}
          style={{ marginRight: "8px" }}
        >
          Crear
        </Button>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <Input
        placeholder="Ingresa un nombre"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, width: 200 }}
        prefix={<SearchOutlined />}
        onKeyDown={handleSearchEnterKey}
      />
      <Button onClick={handleSearch} style={{ marginRight: "8px" }}>
        Buscar
      </Button>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />

      <DeptoForm
        open={showDeptoForm}
        onFinish={saveUserForm}
        onCancel={closeUserForm}
        isEdit={isEdit}
        currentDepto={currentDepto}
      />

      <MessageDialog
        visible={messageVisible}
        title="Está seguro de eliminar este departamento?"
        message="Esta acción no se puede deshacer."
        onOk={deleteUser}
        onCancel={() => setMessageVisible(false)}
        confirm={true}
      />
    </div>
  );
};

export default DepartmentTable;
